import { Component, ElementRef, forwardRef, Input, OnChanges, OnDestroy, OnInit, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { NgbDropdown, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { isEqual } from 'lodash';
import { Observable, of, Subject } from 'rxjs';
import { auditTime, catchError, distinctUntilChanged, switchMap, takeUntil, tap } from 'rxjs/operators';

import { SelectOption as AppSelectOption } from '@app/models';

export type SelectOption = AppSelectOption;

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SelectComponent),
      multi: true
    }
  ],
  standalone: false
})
export class SelectComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy, OnChanges {
  @Input() formControlName: string;
  @Input() value: any = '';
  @Input() name = '';
  @Input() inputClass = '';
  @Input() disabled = false;
  @Input() placeholder = '';
  @Input() required = false;
  @Input() disableSearch = false;
  @Input() isLoading = false;
  @Input() useSelectionMarginOverride = false;
  @Input() options: SelectOption[] = [];
  @Input() menuMaxHeight: string | number = 300;
  @Input() invalid = false;
  @Input() optionsRequest: (search: string) => Observable<SelectOption[]>;
  inputValue = '';
  filteredOptions: SelectOption[] = [];
  selectedOption: SelectOption | null = null;
  activeIndex = -1;
  dropdownOpen = false;
  onChange: (_: string) => void;
  onTouched: () => void;
  onValidationChange: () => void;
  private readonly destroy$ = new Subject<void>();
  private readonly searchChanges$ = new Subject<string>();
  @ViewChild('dropdown', { static: false }) dropdown: NgbDropdown;
  @ViewChild('searchInput', { static: false }) private searchInput?: ElementRef<HTMLInputElement>;
  @ViewChildren(NgbTooltip) private tooltipDirectives?: QueryList<NgbTooltip>;

  constructor(private readonly hostElement: ElementRef<HTMLElement>) {
    this.onChange = () => {};
    this.onTouched = () => {};
    this.onValidationChange = () => {};
  }

  ngOnInit() {
    this.bindRemoteSearch();
    this.syncSelectedOptionFromValue();
    this.updateFilteredOptions();

    if (this.optionsRequest) {
      this.searchChanges$.next(this.getInitialRequestSearch());
    }
  }

  ngOnDestroy() {
    this.closeAllTooltips();
    this.destroy$.next();
    this.destroy$.complete();
    this.searchChanges$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.value && !changes.value.firstChange) {
      this.writeValue(changes.value.currentValue);
      return;
    }

    if (changes.options && !changes.options.firstChange) {
      this.syncSelectedOptionFromValue();
      this.updateFilteredOptions();
      this.resetActiveIndex();
    }

    if (changes.disabled && !changes.disabled.firstChange) {
      this.setDisabledState(changes.disabled.currentValue);
    }

    if ((changes.required && !changes.required.firstChange) || (changes.disableSearch && !changes.disableSearch.firstChange)) {
      this.onValidationChange();
    }
  }

  writeValue(value: any) {
    this.value = value;
    this.syncSelectedOptionFromValue();
    if (!this.dropdownOpen) {
      this.inputValue = '';
    }
    this.updateFilteredOptions();
    this.resetActiveIndex();
  }

  registerOnChange(fn: (_: any) => {}) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => {}) {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void) {
    this.onValidationChange = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  get fieldClassName(): string {
    const classNames = ['field'];

    if (!this.hasVisibleValue) {
      classNames.push('empty');
    }
    if (this.inputClass) {
      classNames.push(this.inputClass);
    }
    if (this.selectedOption?.icon) {
      classNames.push('withIcon');
    }
    if (this.invalid) {
      classNames.push('has-error');
    }

    return classNames.join(' ');
  }

  get hasVisibleValue(): boolean {
    return Boolean(this.inputValue || this.selectedOption);
  }

  get hasOptionsSource(): boolean {
    return Boolean(this.optionsRequest || (this.options || []).length);
  }

  get shouldShowSelectionOverlay(): boolean {
    return Boolean(this.selectedOption && !this.isLoading && (!this.dropdownOpen || this.disableSearch || !this.inputValue));
  }

  onInputInput(event: Event) {
    if (this.disabled || this.isLoading || this.disableSearch) {
      return;
    }

    this.inputValue = (event.target as HTMLInputElement).value || '';
    this.updateFilteredOptions();
    this.resetActiveIndex();

    if (this.optionsRequest) {
      this.searchChanges$.next(this.inputValue);
    }
  }

  onInputKeydown(event: KeyboardEvent) {
    if (this.disabled || this.isLoading) {
      return;
    }

    if (event.key === 'Tab') {
      this.closeDropdown();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDropdown();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!this.dropdownOpen) {
        this.openDropdown();
      } else {
        this.moveActiveIndex(1);
      }
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.dropdownOpen) {
        this.openDropdown();
      } else {
        this.moveActiveIndex(-1);
      }
      return;
    }

    if (event.key === 'Enter') {
      if (!this.dropdownOpen) {
        event.preventDefault();
        this.openDropdown();
        return;
      }

      event.preventDefault();
      const activeOption = this.filteredOptions[this.activeIndex];
      if (activeOption) {
        this.selectOption(activeOption);
      }
    }
  }

  onInputClick() {
    this.openDropdown();
  }

  onInputFocus() {
    this.openDropdown();
  }

  onInputBlur() {
    this.onTouched();
    setTimeout(() => {
      const activeElement = document.activeElement;
      if (this.dropdownOpen && activeElement && !this.hostElement.nativeElement.contains(activeElement)) {
        this.closeDropdown();
      }
    });
  }

  onDropdownOpenChange(isOpen: boolean) {
    this.dropdownOpen = isOpen;

    if (isOpen) {
      this.resetActiveIndex();
      this.focusSearchInput();
      return;
    }

    this.inputValue = '';
    this.updateFilteredOptions();
    this.resetActiveIndex();
  }

  onOptionMouseDown(event: MouseEvent, option: SelectOption) {
    event.preventDefault();
    this.selectOption(option);
  }

  setActiveIndex(index: number) {
    this.activeIndex = index;
  }

  isOptionSelected(option: SelectOption): boolean {
    return this.areOptionKeysEqual(option?.key, this.value);
  }

  trackByOptionIndex(index: number, _option: SelectOption): number {
    return index;
  }

  getFlagLabel(flag?: string): string {
    if (!flag) {
      return '';
    }

    if (flag.toLowerCase() === 'beta') {
      return 'Beta';
    }

    if (flag.toLowerCase() === 'pro') {
      return 'Pro';
    }

    return flag.charAt(0).toUpperCase() + flag.slice(1);
  }

  getFlagBadgeClass(flag?: string): string {
    switch (flag?.toLowerCase()) {
      case 'pro':
        return 'badge-premium';
      case 'beta':
      default:
        return 'badge-error';
    }
  }

  validate(control: UntypedFormControl): ValidationErrors | null {
    return null; // { phoneError: { valid: false } };
  }

  coerceCssSize(v: string | number): string {
    return typeof v === 'number' ? `${v}px` : v;
  }

  private bindRemoteSearch() {
    this.searchChanges$
      .pipe(
        auditTime(300),
        distinctUntilChanged(),
        tap(() => this.updateFilteredOptions()),
        switchMap(search => (this.optionsRequest ? this.optionsRequest(search).pipe(catchError(() => of([] as SelectOption[]))) : of([] as SelectOption[]))),
        takeUntil(this.destroy$)
      )
      .subscribe(options => {
        if (!this.optionsRequest) {
          return;
        }

        this.options = options || [];
        this.syncSelectedOptionFromValue();
        this.updateFilteredOptions();
        this.resetActiveIndex();
      });
  }

  private openDropdown() {
    if (!this.hasOptionsSource || this.disabled || this.isLoading || this.dropdownOpen) {
      return;
    }

    this.dropdown?.open();
  }

  private closeDropdown() {
    this.closeAllTooltips();

    if (!this.dropdownOpen) {
      return;
    }

    this.dropdown?.close();
  }

  private selectOption(option: SelectOption) {
    this.value = option.key;
    this.selectedOption = option;
    this.inputValue = '';
    this.onChange(option.key);
    this.onTouched();
    this.updateFilteredOptions();
    this.closeDropdown();
  }

  private moveActiveIndex(step: number) {
    if (!this.filteredOptions.length) {
      this.activeIndex = -1;
      return;
    }

    if (this.activeIndex < 0) {
      this.activeIndex = step > 0 ? 0 : this.filteredOptions.length - 1;
      return;
    }

    this.activeIndex = Math.max(0, Math.min(this.filteredOptions.length - 1, this.activeIndex + step));
  }

  private resetActiveIndex() {
    if (!this.filteredOptions.length) {
      this.activeIndex = -1;
      return;
    }

    if (this.inputValue) {
      this.activeIndex = 0;
      return;
    }

    const selectedIndex = this.filteredOptions.findIndex(option => this.isOptionSelected(option));
    this.activeIndex = selectedIndex >= 0 ? selectedIndex : 0;
  }

  private syncSelectedOptionFromValue() {
    const matchedOption = (this.options || []).find(option => this.areOptionKeysEqual(option?.key, this.value)) || null;

    if (matchedOption) {
      this.selectedOption = matchedOption;
      return;
    }

    if (this.value == null || this.value === '') {
      this.selectedOption = null;
      return;
    }

    if (this.optionsRequest && this.selectedOption && this.areOptionKeysEqual(this.selectedOption.key, this.value)) {
      return;
    }

    this.selectedOption = null;
  }

  private areOptionKeysEqual(optionKey: any, value: any): boolean {
    return optionKey === value || isEqual(optionKey, value);
  }

  private updateFilteredOptions() {
    const normalizedSearch = this.normalizeText(this.inputValue);
    const visibleOptions = (this.options || []).filter(option => !option?.hidden);

    if (!normalizedSearch) {
      this.filteredOptions = visibleOptions;
      return;
    }

    this.filteredOptions = visibleOptions.filter(option => this.normalizeText(option?.value).includes(normalizedSearch));
  }

  private getInitialRequestSearch(): string {
    return typeof this.value === 'string' ? this.value : '';
  }

  private focusSearchInput() {
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
    });
  }

  private closeAllTooltips() {
    this.tooltipDirectives?.forEach(tooltip => tooltip.close());
    this.removeDetachedTooltips();
  }

  private removeDetachedTooltips() {
    if (typeof document === 'undefined') {
      return;
    }

    document.querySelectorAll('ngb-tooltip-window.select-tooltip').forEach(tooltipWindow => {
      tooltipWindow.remove();
    });
  }

  private getOptionText(option: SelectOption | null | undefined): string {
    return this.parseHTMLToText(option?.value);
  }

  private normalizeText(value: any): string {
    return this.parseHTMLToText(value).trim().toLowerCase();
  }

  private parseHTMLToText(html: any) {
    const element = document.createElement('span');
    element.innerHTML = String(html || '');
    return (element.textContent || element.innerText || '').trim();
  }
}
